import React, { useEffect, useRef, useState } from "react";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatName(name) {
  const yearMonth = String(name).match(/^(\d{4})-(0[1-9]|1[0-2])(-\d{2})?$/);
  if (yearMonth)
    return `${MONTHS[parseInt(yearMonth[2], 10) - 1]} ${yearMonth[1]}`;

  const yearRange = String(name).match(/^(\d{4})-(\d{4})$/);
  if (yearRange) return `${yearRange[1]}\u2013${yearRange[2]}`;

  return name;
}

export default function CoverageTimeline({
  className,
  horizontal,
  coverages,
  checked,
  activeCoverage,
  displayedCoverage,
  sparseCoverages = [],
  playing,
  canPlay,
  onToggle,
  onTogglePlay,
  onSelect,
}) {
  const entries = [
    { id: "Modern", name: "Modern" },
    ...[...coverages]
      .reverse()
      .map((c) => ({ ...c, name: formatName(c.name) })),
  ];

  const list = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(true);
  const updateScrolledToEnd = () => {
    const ol = list.current;
    setScrolledToEnd(ol.scrollLeft + ol.clientWidth >= ol.scrollWidth - 4);
  };

  useEffect(() => {
    if (horizontal) updateScrolledToEnd();
  }, [horizontal, entries.length]);

  // Keep the year being played centered in the horizontal strip
  useEffect(() => {
    if (!horizontal || !playing) return;

    const ol = list.current;
    const item = ol.querySelector(`[data-coverage-id="${activeCoverage}"]`);
    if (!item) return;

    const offset =
      item.getBoundingClientRect().left -
      ol.getBoundingClientRect().left +
      ol.scrollLeft;
    ol.scrollTo({
      left: offset - ol.clientWidth / 2 + item.offsetWidth / 2,
      behavior: "smooth",
    });
  }, [horizontal, playing, activeCoverage]);

  return (
    <div
      className={`${horizontal ? "relative flex items-start" : ""} ${className}`}
    >
      <div
        className={`flex items-center ${
          horizontal
            ? "shrink-0 mr-3 space-x-2"
            : "sticky top-0 z-10 justify-between bg-white pt-3 pb-2"
        }`}
      >
        <label className="flex items-center space-x-1 mb-0 font-normal text-[10px] uppercase tracking-wide text-gray-500 select-none cursor-pointer">
          <input onChange={onToggle} checked={!!checked} type="checkbox" />
          <div>Imagery</div>
        </label>

        <button
          type="button"
          onClick={onTogglePlay}
          disabled={!canPlay}
          aria-label={playing ? "Pause" : "Play through imagery"}
          title={playing ? "Pause" : "Play through imagery"}
          className="flex items-center justify-center w-5 h-5 rounded-full text-gray-500 hover:text-va-blue-700 disabled:text-gray-300 disabled:cursor-default"
        >
          {playing ? (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
              <rect x="1" y="1" width="3" height="8" />
              <rect x="6" y="1" width="3" height="8" />
            </svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
              <path d="M2 1l7 4-7 4z" />
            </svg>
          )}
        </button>
      </div>

      <ol
        ref={list}
        onScroll={horizontal ? updateScrolledToEnd : undefined}
        className={
          horizontal
            ? "flex overflow-x-auto"
            : "relative border-l border-gray-300 ml-1"
        }
      >
        {entries.map((entry) => {
          const active = String(displayedCoverage) === String(entry.id);
          const loading =
            !active && String(activeCoverage) === String(entry.id);
          const disabled =
            !active && !loading && sparseCoverages.includes(entry.id);

          return (
            <li
              key={entry.id}
              data-coverage-id={entry.id}
              className={horizontal ? "relative" : "mb-1 last:mb-0"}
            >
              {horizontal && (
                <span className="absolute top-[4px] left-0 right-0 h-px bg-gray-300" />
              )}

              <button
                type="button"
                disabled={disabled}
                title={
                  disabled
                    ? "Little or no imagery in this area"
                    : entry.attribution || undefined
                }
                onClick={() => {
                  onSelect(active ? null : entry.id);

                  if (!active) {
                    _vaTrack("map.raster_coverage_selected", {
                      event_value: entry.id,
                    });
                  }
                }}
                className={`relative flex text-xs whitespace-nowrap select-none ${
                  disabled
                    ? "cursor-default"
                    : "cursor-pointer hover:text-va-blue-700"
                } ${
                  horizontal
                    ? "flex-col items-center px-3 pt-4 pb-1"
                    : "items-center pl-3 pr-1 py-0.5"
                } ${
                  active
                    ? "font-bold text-va-blue-700"
                    : loading
                      ? "text-va-blue-700"
                      : disabled
                        ? "text-gray-300"
                        : "text-gray-700"
                }`}
              >
                {loading ? (
                  <span
                    className={`absolute w-3 h-3 ${
                      horizontal
                        ? "-top-[2px] left-1/2 -translate-x-1/2"
                        : "-left-[7px] top-1/2 -translate-y-1/2"
                    }`}
                  >
                    <span className="block w-full h-full rounded-full border-2 border-va-blue-300 border-t-va-blue-700 bg-white animate-spin" />
                  </span>
                ) : (
                  <span
                    className={`absolute w-2 h-2 rounded-full border ${
                      horizontal
                        ? "top-0 left-1/2 -translate-x-1/2"
                        : "-left-[5px] top-1/2 -translate-y-1/2"
                    } ${
                      active
                        ? "bg-va-blue border-va-blue"
                        : disabled
                          ? "bg-white border-gray-200"
                          : "bg-white border-gray-400"
                    }`}
                  />
                )}
                {entry.name}
              </button>
            </li>
          );
        })}
      </ol>

      {horizontal && !scrolledToEnd && (
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent" />
      )}
    </div>
  );
}
